/** Keep in sync with MozoMobile/components/dealership/matchOption.ts */
export const MAX_MATCH_OPTIONS = 5;

/**
 * Formats a scored trip match for the add-delivery wizard.
 * The API owns ranking; this only turns DTO fields into scan-able copy.
 * Do not surface pickupOnRoute / detourOk / directionOk here.
 */
export function formatMatchOption(match, recommended) {
    const trip = match.matchedTrip;
    const extra = extraMilesOf(match);
    const pickupOff = offRouteMiles(match.hasSource, match.milesFromSource);
    const dropoffOff = offRouteMiles(match.hasTarget, match.milesFromTarget);

    const chips = [
        extra <= 0
            ? { label: 'No extra miles', tone: 'success' }
            : { label: `+${extra} miles`, tone: 'default' },
    ];

    const schedule = scheduleChip(match);
    if (schedule) {
        chips.push(schedule);
    }

    const scale = scaleChip(match);
    if (scale) {
        chips.push(scale);
    }

    chips.push({ label: vehicleCountLabel(trip), tone: 'default' });

    if (match.canReplaceChase) {
        chips.push({ label: 'Drops chase car', tone: 'success' });
    }

    return {
        title: `Add to D${padTripId(trip?.id ?? 0)} · ${formatDueDate(trip?.requestedDeliveryDateTime)}`,
        route: routeLabel(trip),
        chips,
        why: whyLine(match, extra, pickupOff, dropoffOff, schedule),
        recommended,
    };
}

function scaleChip(match) {
    if (match.scaleOk === false) {
        return null;
    }
    const ratio = match.scaleRatio ?? 1;
    if (ratio >= 0.6) {
        return { label: 'Similar length', tone: 'success' };
    }
    const label = match.existingDurationLabel;
    if (match.existingIsLonger && label) {
        return { label: `Longer trip (${label})`, tone: 'default' };
    }
    if (!match.existingIsLonger && label) {
        return { label: `Shorter trip (${label})`, tone: 'default' };
    }
    return null;
}

function extraMilesOf(match) {
    const n = match.extraMiles ?? match.totalMiles ?? 0;
    return n > 0 ? Math.round(n) : 0;
}

function offRouteMiles(alreadyOnTrip, miles) {
    if (alreadyOnTrip) {
        return 0;
    }
    const n = Math.round(miles ?? 0);
    return n > 0 ? n : 0;
}

function stopName(loc) {
    const d = loc?.Dealership;
    if (!d) {
        return '';
    }
    if (d.dealershipName) {
        return d.dealershipName;
    }
    return [d.city, d.stateAbbr].filter(Boolean).join(', ');
}

function routeLabel(trip) {
    const locs = [...(trip?.TripLocations ?? [])].sort((a, b) => a.locationOrder - b.locationOrder);
    let names = locs.map(stopName).filter(Boolean);
    if (names.length > 2 && names[names.length - 1] === names[0]) {
        names = names.slice(0, -1);
    }
    if (names.length === 0) {
        return '';
    }
    if (names.length > 3) {
        return `${names[0]} → ${names.length - 2} more → ${names[names.length - 1]}`;
    }
    return names.join(' → ');
}

function vehicleCountLabel(trip) {
    const vehs = trip?.TripVehicles ?? [];
    const n = vehs.filter((v) => !v.isChase).length || vehs.length;
    return `${n} vehicle${n === 1 ? '' : 's'}`;
}

function padTripId(id) {
    const s = String(id ?? 0);
    return s.length >= 6 ? s : '0'.repeat(6 - s.length) + s;
}

export function formatDueDate(dt) {
    if (!dt) {
        return '';
    }
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function scheduleChip(match) {
    const hours = match.hoursDifference;
    if (typeof hours === 'number' && Math.abs(hours) <= 14 * 24) {
        if (Math.abs(hours) <= 2) {
            return { label: 'Same window', tone: 'success' };
        }
        if (Math.abs(hours) <= 12) {
            const n = Math.abs(hours);
            const dir = hours > 0 ? 'earlier' : 'later';
            return { label: `${n} hour${n === 1 ? '' : 's'} ${dir}`, tone: 'default' };
        }
        const days = Math.max(1, Math.round(Math.abs(hours) / 24));
        const weekday = formatDueDate(match.matchedTrip?.requestedDeliveryDateTime).split(',')[0];
        const dir = hours > 0 ? 'earlier' : 'later';
        return {
            label: `${weekday}, ${days} day${days === 1 ? '' : 's'} ${dir}`,
            tone: 'warning',
        };
    }
    return dueRelativeChip(match.matchedTrip?.requestedDeliveryDateTime);
}

function dueRelativeChip(iso) {
    if (!iso) {
        return null;
    }
    const due = new Date(iso);
    if (Number.isNaN(due.getTime())) {
        return null;
    }
    const days = Math.round((startOfLocalDay(due).getTime() - startOfLocalDay(new Date()).getTime()) / 86400000);
    if (days === 0) {
        return { label: 'Today', tone: 'success' };
    }
    if (days === 1) {
        return { label: 'Tomorrow', tone: 'default' };
    }
    if (days > 1 && days <= 7) {
        return { label: `In ${days} days`, tone: 'default' };
    }
    return null;
}

function startOfLocalDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Dispatcher-facing impact of joining this trip. Lead with extra miles (the
 * insertion cost the API scored), not corridor error. Percent uses the
 * existing trip length so 20 extra on 40 mi reads differently than 20 on 300.
 */
function whyLine(match, extra, pickupOff, dropoffOff, schedule) {
    const parts = [];
    const bothAlreadyStops = match.hasSource && match.hasTarget;

    if (extra > 0) {
        parts.push(extraMilesSentence(extra, match.matchedTrip?.estimatedMiles));
    } else if (bothAlreadyStops) {
        parts.push('Does not add miles — already stopping at both locations.');
    } else {
        parts.push('Does not add miles to this trip.');
        const offRoute = offRouteClause(pickupOff, dropoffOff);
        if (offRoute) {
            parts.push(offRoute);
        }
    }

    if (schedule?.tone === 'warning') {
        const later = schedule.label.includes('later');
        parts.push(`The existing window is ${later ? 'later' : 'earlier'} than you asked.`);
    }

    return parts.join(' ');
}

function extraMilesSentence(extra, existingMiles) {
    const base = `Adds ${extra} mile${extra === 1 ? '' : 's'} to this trip`;
    const pct = extraPercent(extra, existingMiles);
    if (pct == null) {
        return `${base}.`;
    }
    return `${base} (about ${pct}% longer).`;
}

function extraPercent(extra, existingMiles) {
    if (!(existingMiles > 0) || extra <= 0) {
        return null;
    }
    const pct = Math.round((extra / existingMiles) * 100);
    return pct >= 1 ? pct : null;
}

function offRouteClause(pickupOff, dropoffOff) {
    if (pickupOff === 0 && dropoffOff === 0) {
        return '';
    }
    if (pickupOff === 0) {
        return `Delivery is ${dropoffOff} miles off this route.`;
    }
    if (dropoffOff === 0) {
        return `Pickup is ${pickupOff} miles off this route.`;
    }
    return `Pickup is ${pickupOff} miles off this route. Delivery is ${dropoffOff} miles off.`;
}
